import { BigNum, QUOTE_PRECISION_EXP, ZERO } from '@drift-labs/sdk';
import { MarketId } from '@drift/common';
import { useMemo } from 'react';
import useGetOraclePriceForMarket from '../../hooks/useGetOraclePriceForMarket';
import { useAllVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import {
	getVaultDepositorBalance,
	getVaultDepositorNotionalNetDeposits,
} from '../../utils/vault/math';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { Vault } from '@drift-labs/vaults-sdk';
import { VaultDepositor } from '@drift-labs/vaults-sdk';
import { ZERO_QUOTE_BIGNUM } from 'src/constants/math';
import { UiVaults } from 'src/constants/vaults';
import { SerializedVaultDepositorRecord } from 'src/db/schema/vault-depositor-records';
import ValueWithLabelCard from '../primitives/ValueWithLabelCard/ValueWithLabelCard';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import useIsMobileScreenSize from '../../hooks/useIsMobileScreenSize';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import { useVaultDepositorHistories } from 'src/hooks/query/vaults/base/useVaultDepositorHistories';
import { VaultDepositorAccountWithHistory } from 'src/hooks/query/vaults/types';

export const MyVaultPositionsOverview = () => {
	const spotMarkets = useSpotMarketsLookup();

	const { data: depositorAccounts } = useVaultDepositorAccounts();
	const { data: histories } = useVaultDepositorHistories();

	const vaultDepositorAccounts = useMemo(() => {
		const lookup: Record<string, VaultDepositorAccountWithHistory> = {};
		if (depositorAccounts) {
			Object.entries(depositorAccounts).forEach(([vaultPubkey, depositor]) => {
				lookup[vaultPubkey] = {
					...depositor,
					vaultDepositorHistory: histories?.[vaultPubkey] ?? [],
				};
			});
		}
		return lookup;
	}, [depositorAccounts, histories]);

	const { data: vaultStatsData } = useAllVaultStats();
	const vaultStatsLookup = vaultStatsData.lookup;
	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	const notionalTotalDepositsBalance = useMemo(() => {
		return Object.values(vaultDepositorAccounts).reduce((acc, curr) => {
			const vaultAssetIndex = curr.vault.spotMarketIndex;
			const vaultAssetPrecision = spotMarkets[vaultAssetIndex].precisionExp;
			const vaultStats = vaultStatsLookup[curr.vault.pubkey.toString()];

			if (!vaultStats) return acc;

			const vaultBalance = getVaultDepositorBalance(
				curr.vaultDepositor,
				{
					totalShares: curr.vault.totalShares,
					profitShare: curr.vault.profitShare,
				},
				vaultStats.tvlBase,
				vaultAssetPrecision,
				true
			);

			const oraclePrice = getOraclePriceForMarket(
				MarketId.createSpotMarket(vaultAssetIndex)
			);

			const notionalValueQuote = vaultBalance
				.mul(oraclePrice ?? ZERO_QUOTE_BIGNUM)
				.shiftTo(QUOTE_PRECISION_EXP);

			return acc.add(notionalValueQuote);
		}, BigNum.zero(QUOTE_PRECISION_EXP));
	}, [
		vaultDepositorAccounts,
		getOraclePriceForMarket,
		vaultStatsLookup,
		spotMarkets,
	]);
	const numberOfUnfetchedVaultDepositorHistories = useMemo(() => {
		return Object.values(vaultDepositorAccounts).filter(
			(vaultDepositorAccount) => !vaultDepositorAccount.vaultDepositorHistory
		).length;
	}, [vaultDepositorAccounts]);

	const notionalTotalPnl = useMemo(() => {
		const vaultDepositorAccountsList = Object.values(vaultDepositorAccounts);

		const simplePnlCalculation = ({
			vaultDepositor,
			vault,
		}: {
			vaultDepositor: VaultDepositor;
			vault: Vault;
		}) => {
			const vaultAssetIndex = vault.spotMarketIndex;
			const vaultAssetPrecision =
				spotMarkets[vaultAssetIndex]?.precisionExp ?? ZERO;
			const vaultStats = vaultStatsLookup[vault.pubkey.toString()];

			if (!vaultStats) return ZERO_QUOTE_BIGNUM;

			const vaultDepositorBalance = getVaultDepositorBalance(
				vaultDepositor,
				{
					totalShares: vault.totalShares,
					profitShare: vault.profitShare,
				},
				vaultStats.tvlBase,
				vaultAssetPrecision,
				true
			);

			const totalPnL = vaultDepositorBalance.sub(
				BigNum.from(vaultDepositor.netDeposits as any, vaultAssetPrecision)
			);

			const oraclePrice = getOraclePriceForMarket(
				MarketId.createSpotMarket(vaultAssetIndex)
			);

			const notionalPnlQuote = totalPnL
				.mul(oraclePrice ?? ZERO_QUOTE_BIGNUM)
				.shiftTo(QUOTE_PRECISION_EXP);

			return notionalPnlQuote;
		};

		const notionalGrowthStrategyPnlCalculation = ({
			vaultDepositor,
			vault,
			vaultDepositorHistory,
		}: {
			vaultDepositor: VaultDepositor;
			vault: Vault;
			vaultDepositorHistory: SerializedVaultDepositorRecord[];
		}) => {
			const userNotionalNetDeposits = getVaultDepositorNotionalNetDeposits(
				vaultDepositorHistory
			);

			const vaultAssetIndex = vault.spotMarketIndex;
			const vaultAssetPrecision =
				spotMarkets[vaultAssetIndex]?.precisionExp ?? ZERO;
			const vaultStats = vaultStatsLookup[vault.pubkey.toString()];

			if (!vaultStats) return ZERO_QUOTE_BIGNUM;

			const vaultDepositorBalance = getVaultDepositorBalance(
				vaultDepositor,
				{
					totalShares: vault.totalShares,
					profitShare: vault.profitShare,
				},
				vaultStats.tvlBase,
				vaultAssetPrecision
			);
			const oraclePrice = getOraclePriceForMarket(
				MarketId.createSpotMarket(vaultAssetIndex)
			);
			const userNotionalBalance = vaultDepositorBalance
				.mul(oraclePrice ?? ZERO_QUOTE_BIGNUM)
				.shiftTo(QUOTE_PRECISION_EXP);

			const netNotionalPnl = userNotionalBalance.sub(userNotionalNetDeposits);

			return netNotionalPnl;
		};
		const totalPnl = vaultDepositorAccountsList.reduce((total, account) => {
			const uiVaultConfig = UiVaults.getVaultConfig(
				account.vault.pubkey.toString()
			);

			if (!uiVaultConfig) return total;

			const pnl = uiVaultConfig.isNotionalGrowthStrategy
				? notionalGrowthStrategyPnlCalculation({
						vaultDepositor: account.vaultDepositor,
						vault: account.vault,
						vaultDepositorHistory: (account.vaultDepositorHistory as any) ?? [],
					})
				: simplePnlCalculation(account);

			return total.add(pnl);
		}, ZERO_QUOTE_BIGNUM);

		return totalPnl;
	}, [
		vaultDepositorAccounts,
		spotMarkets,
		vaultStatsLookup,
		getOraclePriceForMarket,
		numberOfUnfetchedVaultDepositorHistories,
	]);

	const isMobile = useIsMobileScreenSize();

	if (isMobile) {
		return (
			<ValueWithLabelCard.Container className="border-none bg-container-bg">
				<ValueWithLabelCard.Item
					label={
						<DefinitionTooltip definitionId="strategyVaultsYourBalance">
							<span className="typo-b3">Your Balance</span>
						</DefinitionTooltip>
					}
				>
					${notionalTotalDepositsBalance.toMillified(4)}
				</ValueWithLabelCard.Item>
				<ValueWithLabelCard.Item
					label={
						<DefinitionTooltip definitionId="strategyVaultsYourEarnings">
							<span className="typo-b3">Your Earnings</span>
						</DefinitionTooltip>
					}
				>
					${notionalTotalPnl?.toMillified(4) ?? 0}
				</ValueWithLabelCard.Item>
			</ValueWithLabelCard.Container>
		);
	}

	return (
		<div className="flex items-center gap-sm">
			<ValueWithLabelCard.SingleItemCard
				className="sm:px-md sm:py-sm"
				label={
					<DefinitionTooltip definitionId="strategyVaultsYourBalance">
						<span className="typo-b3">Your Balance</span>
					</DefinitionTooltip>
				}
			>
				<span className="typo-t3">
					${notionalTotalDepositsBalance.toMillified(4)}
				</span>
			</ValueWithLabelCard.SingleItemCard>
			<ValueWithLabelCard.SingleItemCard
				className="sm:px-md sm:py-sm"
				label={
					<DefinitionTooltip definitionId="strategyVaultsYourEarnings">
						<span className="typo-b3">Your Earnings</span>
					</DefinitionTooltip>
				}
			>
				<span className="typo-t3">
					${notionalTotalPnl?.toMillified(4) ?? 0}
				</span>
			</ValueWithLabelCard.SingleItemCard>
		</div>
	);
};
